<script setup lang="ts">
import { ref } from "vue";
import { ClampVariableRule } from "@/modules/rule_engine/ruleEngineClasses";
import { parseRule } from "@/modules/rule_engine/parser";
import { useRuleEnginedStore } from "@/modules/rule_engine/store";
import {
	required,
	numericAndFloat,
	largerThan,
} from "@/ui/validation/validationRules";
import { useI18n } from "vue-i18n";
import ConfigureConditions from "@/modules/rule_engine/components/conditions/ConfigureConditions.vue";
import { toRawDeep } from "@/Utils/to-raw-deep";

const { t } = useI18n();

const emit = defineEmits(["store"]);
const form = ref();

const props = defineProps<{
	rule: ClampVariableRule;
}>();

const isDialogVisible = defineModel<boolean>();
const isFormValid = ref<boolean>(false);

// this JSON.. hurts, but because of the class usage spreading doesn't work properly
const clampVariableRule = ref<ClampVariableRule>(
	parseRule<"clamp_variable">({
		...structuredClone(toRawDeep(props.rule)),
		type: "clamp_variable",
	})
);

const storeClampVariableRule = async () => {
	// validate input before storing it in the model
	const result = await form.value?.validate();
	if (!result?.valid) return;

	emit("store", clampVariableRule.value);
	isDialogVisible.value = false;
};
</script>

<template>
	<v-dialog v-model="isDialogVisible" persistent width="70vw">
		<v-form ref="form">
			<v-card elevation="9" class="help">
				<v-toolbar>
					<v-toolbar-title>Configure Clamp Variable Rule</v-toolbar-title>
					<v-spacer></v-spacer>

					<v-btn icon @click="isDialogVisible = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-toolbar>

				<v-divider />

				<div class="pa-5">
					<configure-conditions v-model="clampVariableRule.conditions" />

					<v-card-title>Clamp:</v-card-title>
					<v-card class="pa-5">
						<div class="d-flex w-100">
							<div class="mt-3">Variable:</div>
							<div class="ml-3">
								<v-select
									hide-details="auto"
									width="250"
									variant="filled"
									v-model="clampVariableRule.variable_name"
									:items="useRuleEnginedStore().availableActionVariables"
									:item-title="
										(item) => t(`rule_engine.action_variables.${item.name}`)
									"
									:item-value="(item) => item.name"
									:rules="[required(t)]"
								/>
							</div>
							<div class="ml-3 mt-3">between</div>
							<div class="ml-3">
								<v-text-field
									hide-details="auto"
									width="150"
									variant="filled"
									v-model="clampVariableRule.min_value"
									:rules="[required(t), numericAndFloat(t)]"
								></v-text-field>
							</div>
							<div class="ml-3 mt-3">and</div>
							<div class="ml-3">
								<v-text-field
									hide-details="auto"
									width="150"
									variant="filled"
									v-model="clampVariableRule.max_value"
									:rules="[
										required(t),
										numericAndFloat(t),
										largerThan(t, clampVariableRule.min_value),
									]"
								></v-text-field>
							</div>
						</div>
					</v-card>
				</div>

				<v-divider class="mt-2" />

				<v-card-actions class="pa-3">
					<v-spacer />
					<v-btn variant="text" color="error" @click="isDialogVisible = false"
						>Cancel</v-btn
					>
					<v-btn
						class="ml-5"
						variant="tonal"
						color="primary"
						@click="storeClampVariableRule"
						>Store Rule</v-btn
					>
				</v-card-actions>
			</v-card>
		</v-form>
	</v-dialog>
</template>

<style scoped lang="scss">
.condition {
	border-left: 5px solid #e67e22;
}
</style>
